import { createSelector } from '@reduxjs/toolkit';
import { compact, isEmpty, isNil, omit, omitBy, pick } from 'lodash';
import { add, formatISO, nextSaturday, parse } from 'date-fns';
import { FORMAT_QT_DATE } from 'utils/helpers/datetime';
import { getSegments } from 'redux/selectors/sealink';
import { getAllPassengerTypes } from '../../booking_form';
import { getCurrentBooking, isBookingOneWay } from '../../current_booking';

const parseDate = (date) => {
  if (date == null) {
    return null;
  }
  return parse(date.split('T')[0], FORMAT_QT_DATE, new Date());
};

const getSealinkUi = (state) => state?.ui?.sealink;

const getFirstFerryGrouping = createSelector([getSealinkUi], (sealink) => {
  const ferryGrouping = sealink?.ferryGrouping || [];
  return ferryGrouping[0]?.values || null;
});

const buildJourneySegments = (
  isPrimaryOnly,
  journeySegments,
  segmentStops,
  allSegments
) => {
  let previousSegment = null;

  return compact(
    journeySegments.map((segmentId) => {
      const segment = allSegments.find((o) => o.id === segmentId);

      if (isPrimaryOnly && !segment.isPrimarySegment) {
        return null;
      }

      const result = {
        ...segment,
        previousSegment,
        selectedRouteStops: compact([
          segmentStops[segmentId]?.departureRouteStop,
          segmentStops[segmentId]?.arrivalRouteStop,
        ]),
      };
      previousSegment = { ...omit(result, 'previousSegment') };
      return result;
    })
  );
};

const getFirstJourneyForm = createSelector(
  [getFirstFerryGrouping, getSegments],
  (grouping, segments) => {
    const form = grouping?.journeyForm || null;
    if (form == null) {
      return null;
    }

    const [departureDate, returnDate] = form?.journeyDates || [];
    const [departureJourney, returnJourney] = form?.journeys || [];

    return {
      isReturn: form.journeyDates.length > 1,
      requestedVehicle: form.takingVehicle,
      requestedShuttle: form.shuttleTransport,
      serviceRouteIndex: form.serviceRouteIndex,
      forward: {
        date: parseDate(departureDate),
        segments: buildJourneySegments(
          !form.shuttleTransport,
          departureJourney?.segments || [],
          departureJourney?.segmentStops || [],
          segments
        ),
      },
      return: {
        date: parseDate(returnDate),
        segments: buildJourneySegments(
          !form.shuttleTransport,
          returnJourney?.segments || [],
          returnJourney?.segmentStops || [],
          segments
        ),
      },
    };
  }
);

const getJourneys = createSelector(
  [getFirstFerryGrouping, getSegments],
  (grouping, segments) => {
    const form = grouping?.journeyForm || null;
    if (form == null) {
      return [];
    }

    return form.journeys.map((journey) => ({
      name: journey.name,
      segments: buildJourneySegments(
        form.requestedShuttle,
        journey.segments,
        journey.segmentStops,
        segments
      ),
    }));
  }
);

const getPassengersFromFirstFerryGrouping = createSelector(
  getFirstFerryGrouping,
  (grouping) => grouping?.passengerForm?.passengers || {}
);

const ecomTypes = window.ecom.craftConfig.accommodationPassengerTypes;
const AVAILABLE_PASSENGER_TYPES = ecomTypes?.length > 0 ? ecomTypes : [1, 2];
const accommodationFlowAvailablePassengerTypes = createSelector(
  getAllPassengerTypes,
  (passengerTypes) => pick(passengerTypes, AVAILABLE_PASSENGER_TYPES)
);

/**
 * Return default guests based on passenger form
 *  - Use same passenger types if has ferry
 *  - Set 2 Adult as default if do not has ferry
 */
const accommodationFlowDefaultGuests = createSelector(
  accommodationFlowAvailablePassengerTypes,
  getPassengersFromFirstFerryGrouping,
  (passengerTypes, passengers) =>
    Object.values(passengerTypes).reduce((obj, type) => {
      if (type.name === 'Adult') {
        obj[type.name] = passengers[type.id] || 2;
      } else {
        obj[type.name] = passengers[type.id] || 0;
      }
      return obj;
    }, {})
);

/**
 * Return default dates based on booking journey dates
 *  - With out booking, the dates should be weekend
 *  - With return ferry on different days, the dates should be same as ferry
 *  - With return ferry on the same day, the dates should be weekend
 *  - With one way ferry, the check-in date should be same as ferry, checkout-out date should be 1 day after
 */
const accommodationFlowDefaultDates = createSelector(
  getCurrentBooking,
  isBookingOneWay,
  (currentBooking, isOneWayBooking) => {
    const defaultCheckIn = nextSaturday(new Date());
    const datesWithOutBooking = {
      checkInDate: formatISO(defaultCheckIn, { representation: 'date' }),
      checkOutDate: formatISO(add(defaultCheckIn, { days: 1 }), {
        representation: 'date',
      }),
    };
    const datesBasedOnBooking = {
      checkInDate: currentBooking.first_travel_date?._value,
      checkOutDate: currentBooking.last_travel_date?._value,
    };

    if (isOneWayBooking) {
      datesBasedOnBooking.checkOutDate = formatISO(
        add(new Date(datesBasedOnBooking.checkInDate), { days: 1 }),
        {
          representation: 'date',
        }
      );
    } else if (
      datesBasedOnBooking.checkInDate === datesBasedOnBooking.checkOutDate
    ) {
      datesBasedOnBooking.checkInDate = null;
      datesBasedOnBooking.checkOutDate = null;
    }

    return {
      ...datesWithOutBooking,
      ...omitBy(datesBasedOnBooking, isNil),
    };
  }
);

const accommodationFlowDefaultSearchParams = createSelector(
  [accommodationFlowDefaultGuests, accommodationFlowDefaultDates],
  (defaultGuests, defaultDates) => ({
    ...defaultDates,
    location: -1,
    region: 1,
    guests: defaultGuests,
  })
);

/*
We should display on the departure ferry service screen
(only if the journey is going to Rottnest Island - ie. not a one-way from the Island.).
At that point, we have the passenger breakdown to display.
*/
const getAdmissionFeeDetail = createSelector([getCurrentBooking], (booking) => {
  const { categories } =
    ecom.craftConfig.bookingPreferences?.admissionFees || {};
  const result = {
    show: false,
    totalPrice: 0,
    priceList: {},
  };

  if (isEmpty(booking) || isEmpty(categories)) {
    return result;
  }

  const admissionFeeIds = categories.map((item) => item.id);
  const allAdjustmentsAttributes = booking.reservations_attributes
    .map((res) => res.adjustments_attributes)
    .flat();
  const admissionAttributes = allAdjustmentsAttributes.filter((item) =>
    admissionFeeIds.includes(item.adjustment_category_id)
  );

  if (!admissionAttributes.length) {
    return result;
  }

  /*
  Get any text between two `-`
  `Admission Fee - Adult - Return on` -> `Adult`
  `Admission Fee - Child - Return on` -> `Child`
  */
  const pattern = /- ([^]*?) -/;

  admissionAttributes.forEach((admission) => {
    const matchResult = admission.description.match(pattern);
    const type = matchResult?.[1];

    if (!type) {
      return;
    }
    result.totalPrice += admission.gross_in_cents;
    // Accumulate admission fees per passenger type (not overwrite)
    result.priceList[type] =
      (result.priceList[type] || 0) + admission.gross_in_cents;
  });
  result.show = true;

  return result;
});

export {
  getFirstFerryGrouping,
  getFirstJourneyForm,
  getJourneys,
  accommodationFlowDefaultGuests,
  accommodationFlowDefaultDates,
  accommodationFlowDefaultSearchParams,
  accommodationFlowAvailablePassengerTypes,
  getAdmissionFeeDetail,
};
